import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import Checkbox from '@bibliocommons/base-checkbox';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Arrows } from '@bibliocommons/deprecated-base-icons';
import { getLabel } from '@bibliocommons/utils-filter';
import { routerShape, searchFieldShape } from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';

import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import HelpLinkContainer from 'app/components/shared/HelpLink/HelpLinkContainer';
import formatFilterForQuery from 'app/helpers/facets/formatFilterForQuery';
import formatFiltersForQuery from 'app/helpers/facets/formatFiltersForQuery';
import ExpandFilterLink from '../ExpandFilterLink';

import './LanguageField.scss';

export class LanguageField extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      translationsIncluded: false,
      isExpanded: false
    };
    bindAll(this);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (!prevState.isExpanded && this.state.isExpanded && this._checkboxRef) {
      this._checkboxRef.focus();
    }
  }

  handleLanguageToggle(fieldId) {
    const { router, activeFilters, transformQuery } = this.props;
    const filters = activeFilters.get('LANGUAGE') || activeFilters.get('PRIMARY_LANGUAGE');

    if (filters) {
      let query = {
        ...router.query,
        ...formatFiltersForQuery(activeFilters, FILTER_SEPARATOR, FILTER_PREFIX),
        f_LANGUAGE: undefined,
        f_PRIMARY_LANGUAGE: undefined,
        ...formatFilterForQuery(fieldId, filters, FILTER_SEPARATOR, FILTER_PREFIX)
      };

      if (transformQuery) {
        query = transformQuery(query);
      }

      router.push({ query });
    }
  }

  toggleTranslationsIncluded() {
    // eslint-disable-next-line react/no-access-state-in-setstate
    const newToggleState = !this.state.translationsIncluded;
    this.setState({
      translationsIncluded: newToggleState
    });
    const fieldId = newToggleState ? 'LANGUAGE' : 'PRIMARY_LANGUAGE';

    return this.handleLanguageToggle(fieldId);
  }

  handleToggleFilter(checked, { value }) {
    this.props.handleToggleFilter(this.getCurrentFieldId(), value);
  }

  getCurrentFieldId() {
    return this.state.translationsIncluded && this.props.language
      ? this.props.language.get('id')
      : this.props.primaryLanguage.get('id');
  }

  expandField(e) {
    e.preventDefault();
    this.setState(state => ({
      isExpanded: !state.isExpanded
    }));
  }

  renderExpandIcon() {
    if (this.state.isExpanded) {
      return <Arrows.Medium.Up color="#333" className="small" />;
    }

    return <Arrows.Medium.Down color="#333" className="small" />;
  }

  renderFilter(filter) {
    const value = filter.get('value');
    const label = filter.get('label');
    const currentFieldId = this.getCurrentFieldId();
    const renderKey = `${currentFieldId}-${value}`;
    if (!this.state.isExpanded) {
      return <span key={renderKey} />;
    }

    const filterLabel = getLabel(this.props.intl, 'LANGUAGE', label || value);
    const formattedLabel = this.props.intl.formatMessage(
      { id: 'Facets.filterLabelFormat' },
      { value: filterLabel, count: filter.get('count') }
    );

    return (
      <li key={renderKey} className="filter">
        <Checkbox
          checked={filter.get('applied')}
          handleChange={this.handleToggleFilter}
          id={`field-${value}`}
          label={formattedLabel}
          value={filter}
        />
      </li>
    );
  }

  renderFilters() {
    const currentField = this.state.translationsIncluded ? this.props.language : this.props.primaryLanguage;
    const visibleFilter = (f, index) => index < 15 || f.get('applied');
    return currentField
      .get('fieldFilters')
      .filter(visibleFilter)
      .map(this.renderFilter);
  }

  renderFieldToggle() {
    if (!this.state.isExpanded) {
      return <span />;
    }
    return (
      <li className="language-toggle" key="language-toggle-checkbox">
        <Checkbox
          checked={this.state.translationsIncluded}
          handleChange={this.toggleTranslationsIncluded}
          id="language-translation-toggle"
          label={this.props.intl.formatMessage({ id: 'include_translations' })}
          innerRef={el => (this._checkboxRef = el)}
        />
        <HelpLinkContainer className="small help-link" page="045faq/030faq-searching/#trans">
          <ScreenReaderMessage>
            <FormattedMessage id="ada_include_translations_help_link" />
          </ScreenReaderMessage>
        </HelpLinkContainer>
        <hr />
      </li>
    );
  }

  renderExpandFilterLink() {
    const currentField = this.state.translationsIncluded ? this.props.language : this.props.primaryLanguage;
    if (currentField.get('hasMore') && this.state.isExpanded) {
      return (
        <ExpandFilterLink
          fieldId={currentField.get('id')}
          filters={currentField.get('fieldFilters')}
          label="LANGUAGE.field"
          handleToggleExpandedField={this.props.handleToggleExpandedField}
          handleCloseExpandField={this.props.handleCloseExpandField}
          handleExpandField={this.props.handleExpandField}
        />
      );
    }
    return null;
  }

  render() {
    if (this.props.primaryLanguage ? !this.props.language : this.props.language) {
      return null;
    }
    if (this.props.primaryLanguage || this.props.language) {
      return (
        <section className="cp-language-field">
          <a href="#expand-filter" onClick={this.expandField} className="field-toggle">
            <span className="field-text field-name">
              <FormattedMessage id="LANGUAGES.field" />
            </span>
            {this.renderExpandIcon()}
          </a>
          <ul className="filter-list">
            {this.renderFieldToggle()}
            {this.renderFilters()}
          </ul>
          {this.renderExpandFilterLink()}
        </section>
      );
    }
    return null;
  }
}

LanguageField.displayName = 'LanguageField';

LanguageField.propTypes = {
  primaryLanguage: searchFieldShape,
  language: searchFieldShape,
  activeFilters: ImmutablePropTypes.map,
  handleToggleFilter: PropTypes.func,
  handleToggleExpandedField: PropTypes.func,
  handleCloseExpandField: PropTypes.func,
  handleExpandField: PropTypes.func,
  transformQuery: PropTypes.func,
  router: routerShape.isRequired
};

export default injectIntl(LanguageField);
